import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Radar, Bell, Target, Shield, Lock, Save, Briefcase, Users } from "lucide-react";
import Navbar from "@/components/Navbar";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isEmployer, isAgency } from "@/lib/roles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

function Row({ title, desc, children }) {
  return (
    <div className="flex items-center justify-between gap-4 py-4 border-b border-slate-800 last:border-0">
      <div>
        <div className="text-slate-200 font-medium text-sm">{title}</div>
        {desc && <div className="text-slate-500 text-xs mt-0.5">{desc}</div>}
      </div>
      {children}
    </div>
  );
}

const EMP_DEFAULTS = {
  hiring: { default_location: "", default_categories: [], auto_shortlist_match: 85, team_notify_email: true },
  notifications: { new_applicant: true, candidate_match: true, weekly_digest: true },
  agency: { default_client_id: "", share_pool_across_clients: true, commission_pct: 15, acting_on_behalf: true },
};

export default function Settings() {
  const { user } = useAuth();
  const { t: tr } = useTranslation();
  const employer = isEmployer(user?.role);
  const agency = isAgency(user?.role);
  const [s, setS] = useState(null);
  const [clients, setClients] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => {
      let data = r.data || {};
      if (employer) data = { ...EMP_DEFAULTS, ...data, hiring: { ...EMP_DEFAULTS.hiring, ...(data.hiring || {}) }, notifications: { ...EMP_DEFAULTS.notifications, ...(data.notifications || {}) }, agency: { ...EMP_DEFAULTS.agency, ...(data.agency || {}) } };
      setS(data);
    });
    if (agency) api.get("/agency/clients").then((r) => setClients(r.data)).catch(() => {});
  }, [employer, agency]);

  const upd = (group, key, val) => setS((prev) => ({ ...prev, [group]: { ...prev[group], [key]: val } }));

  const save = async () => {
    setSaving(true);
    try { await api.put("/settings", { settings: s }); toast.success(tr("settings.saved")); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!s) return (<div className="min-h-screen wo-bg"><Navbar /><p className="text-slate-500 text-center py-20">{tr("radar.scanning")}</p></div>);

  const seekerTabs = [
    { v: "radar", label: "Radar & Location", icon: Radar },
    { v: "notifications", label: "Notifications", icon: Bell },
    { v: "preferences", label: "Job Preferences", icon: Target },
    { v: "privacy", label: "Privacy", icon: Shield },
    { v: "account", label: "Account", icon: Lock },
  ];
  const employerTabs = [
    { v: "hiring", label: "Hiring", icon: Briefcase },
    { v: "notifications", label: "Notifications", icon: Bell },
    ...(agency ? [{ v: "agency", label: "Agency", icon: Users }] : []),
    { v: "account", label: "Account", icon: Lock },
  ];
  const tabs = employer ? employerTabs : seekerTabs;

  return (
    <div className="min-h-screen wo-bg">
      <Navbar />
      <div className="max-w-4xl mx-auto px-5 sm:px-8 py-8 pb-28 lg:pb-8">
        <h1 className="font-display font-bold text-3xl text-slate-100 mb-1">{tr("settings.title")}</h1>
        <p className="text-slate-400 text-sm mb-6">{employer ? (agency ? "Agency workspace settings" : "Company hiring settings") : "Your job-seeker preferences"}</p>

        <Tabs defaultValue={tabs[0].v} className="w-full">
          <TabsList className="liquid-glass flex flex-wrap h-auto gap-1 p-1 mb-6">
            {tabs.map((t) => (
              <TabsTrigger key={t.v} value={t.v} data-testid={`settings-tab-${t.v}`}
                className="data-[state=active]:bg-teal-500/15 data-[state=active]:text-teal-300 text-slate-400 gap-2 text-xs sm:text-sm">
                <t.icon size={14} /> <span className="hidden sm:inline">{t.label}</span>
              </TabsTrigger>
            ))}
          </TabsList>

          <div className="liquid-glass rounded-2xl p-6">
            {/* ---------- SEEKER ---------- */}
            {!employer && <>
              <TabsContent value="radar" className="mt-0">
                <Row title={tr("settings.searchRadius")} desc={tr("settings.searchRadiusD")}>
                  <div className="flex items-center gap-3 w-48"><Slider data-testid="settings-radius-slider" value={[s.radar.radius_km]} onValueChange={([v]) => upd("radar", "radius_km", v)} min={1} max={50} step={1} /><span className="text-teal-400 font-mono text-sm w-14 text-right">{s.radar.radius_km}km</span></div>
                </Row>
                <Row title={tr("settings.proximity")} desc={tr("settings.proximityD")}>
                  <div className="flex items-center gap-3 w-48"><Slider value={[s.radar.proximity_alert_km]} onValueChange={([v]) => upd("radar", "proximity_alert_km", v)} min={1} max={50} step={1} /><span className="text-teal-400 font-mono text-sm w-14 text-right">{s.radar.proximity_alert_km}km</span></div>
                </Row>
                <Row title={tr("settings.livePulse")} desc={tr("settings.livePulseD")}><Switch data-testid="settings-live-pulse" checked={s.radar.live_pulse} onCheckedChange={(v) => upd("radar", "live_pulse", v)} /></Row>
              </TabsContent>
              <TabsContent value="notifications" className="mt-0">
                <Row title={tr("settings.emailDigest")} desc={tr("settings.emailDigestD")}><Switch data-testid="settings-email-digest" checked={s.notifications.email_digest} onCheckedChange={(v) => upd("notifications", "email_digest", v)} /></Row>
                <Row title={tr("settings.instantMatch")} desc={tr("settings.instantMatchD")}><Switch checked={s.notifications.instant_match} onCheckedChange={(v) => upd("notifications", "instant_match", v)} /></Row>
                <Row title={tr("settings.appUpdates")} desc={tr("settings.appUpdatesD")}><Switch checked={s.notifications.application_updates} onCheckedChange={(v) => upd("notifications", "application_updates", v)} /></Row>
              </TabsContent>
              <TabsContent value="preferences" className="mt-0">
                <div className="py-4 border-b border-slate-800">
                  <Label className="text-slate-200 text-sm">{tr("settings.targetTitles")}</Label>
                  <Input data-testid="settings-target-titles" value={(s.preferences.target_titles || []).join(", ")} onChange={(e) => upd("preferences", "target_titles", e.target.value.split(",").map((x) => x.trim()).filter(Boolean))} placeholder="Frontend Engineer, Product Designer" className="mt-1.5 bg-slate-950/50 border-slate-700 text-slate-100" />
                </div>
                <Row title={tr("settings.minSalaryExp")} desc={`$${Math.round(s.preferences.min_salary / 1000)}k`}>
                  <div className="w-48"><Slider value={[s.preferences.min_salary]} onValueChange={([v]) => upd("preferences", "min_salary", v)} min={0} max={300000} step={10000} /></div>
                </Row>
                <div className="py-4">
                  <Label className="text-slate-200 text-sm">{tr("settings.techStack")}</Label>
                  <Input value={(s.preferences.tech_stack || []).join(", ")} onChange={(e) => upd("preferences", "tech_stack", e.target.value.split(",").map((x) => x.trim()).filter(Boolean))} placeholder="React, Python, AWS" className="mt-1.5 bg-slate-950/50 border-slate-700 text-slate-100" />
                </div>
              </TabsContent>
              <TabsContent value="privacy" className="mt-0">
                <Row title={tr("settings.publicResume")} desc={tr("settings.publicResumeD")}><Switch data-testid="settings-public-resume" checked={s.privacy.public_resume} onCheckedChange={(v) => upd("privacy", "public_resume", v)} /></Row>
                <Row title={tr("settings.stealth")} desc={tr("settings.stealthD")}><Switch checked={s.privacy.stealth_mode} onCheckedChange={(v) => upd("privacy", "stealth_mode", v)} /></Row>
                <Row title={tr("settings.activity")} desc={tr("settings.activityD")}><Switch checked={s.privacy.activity_status} onCheckedChange={(v) => upd("privacy", "activity_status", v)} /></Row>
              </TabsContent>
            </>}

            {/* ---------- EMPLOYER (company + agency) ---------- */}
            {employer && <>
              <TabsContent value="hiring" className="mt-0">
                <div className="py-4 border-b border-slate-800">
                  <Label className="text-slate-200 text-sm">Default hiring location</Label>
                  <Input data-testid="settings-hiring-location" value={s.hiring.default_location} onChange={(e) => upd("hiring", "default_location", e.target.value)} placeholder="e.g. London, UK" className="mt-1.5 bg-slate-950/50 border-slate-700 text-slate-100" />
                </div>
                <div className="py-4 border-b border-slate-800">
                  <Label className="text-slate-200 text-sm">Focus categories</Label>
                  <Input data-testid="settings-hiring-categories" value={(s.hiring.default_categories || []).join(", ")} onChange={(e) => upd("hiring", "default_categories", e.target.value.split(",").map((x) => x.trim()).filter(Boolean))} placeholder="Engineering, Design, Data" className="mt-1.5 bg-slate-950/50 border-slate-700 text-slate-100" />
                </div>
                <Row title="Auto-shortlist threshold" desc="Automatically flag applicants scoring at or above this match">
                  <div className="flex items-center gap-3 w-48"><Slider data-testid="settings-auto-shortlist" value={[s.hiring.auto_shortlist_match]} onValueChange={([v]) => upd("hiring", "auto_shortlist_match", v)} min={50} max={100} step={1} /><span className="text-teal-400 font-mono text-sm w-12 text-right">{s.hiring.auto_shortlist_match}%</span></div>
                </Row>
                <Row title="Notify my team by email" desc="Send new-candidate emails to your hiring team"><Switch data-testid="settings-team-notify" checked={s.hiring.team_notify_email} onCheckedChange={(v) => upd("hiring", "team_notify_email", v)} /></Row>
              </TabsContent>
              <TabsContent value="notifications" className="mt-0">
                <Row title="New applicant alerts" desc="When someone applies to your postings"><Switch data-testid="settings-emp-new-applicant" checked={s.notifications.new_applicant} onCheckedChange={(v) => upd("notifications", "new_applicant", v)} /></Row>
                <Row title="Candidate match alerts" desc="When a strong candidate joins the talent pool"><Switch checked={s.notifications.candidate_match} onCheckedChange={(v) => upd("notifications", "candidate_match", v)} /></Row>
                <Row title="Weekly digest" desc="A weekly hiring summary by email"><Switch checked={s.notifications.weekly_digest} onCheckedChange={(v) => upd("notifications", "weekly_digest", v)} /></Row>
              </TabsContent>
              {agency && (
                <TabsContent value="agency" className="mt-0">
                  <div className="py-4 border-b border-slate-800">
                    <Label className="text-slate-200 text-sm">Default client</Label>
                    <Select value={s.agency.default_client_id || "none"} onValueChange={(v) => upd("agency", "default_client_id", v === "none" ? "" : v)}>
                      <SelectTrigger data-testid="settings-default-client" className="mt-1.5 bg-slate-950/50 border-slate-700 text-slate-200"><SelectValue placeholder="No default" /></SelectTrigger>
                      <SelectContent className="text-slate-200">
                        <SelectItem value="none">No default</SelectItem>
                        {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500 mt-1">Pre-selected when posting jobs or submitting candidates.</p>
                  </div>
                  <Row title="Share talent pool across clients" desc="Reuse saved candidates for any client you recruit for"><Switch data-testid="settings-share-pool" checked={s.agency.share_pool_across_clients} onCheckedChange={(v) => upd("agency", "share_pool_across_clients", v)} /></Row>
                  <Row title="Acting on behalf of clients" desc="Postings represent your client companies, not your agency"><Switch checked={s.agency.acting_on_behalf} onCheckedChange={(v) => upd("agency", "acting_on_behalf", v)} /></Row>
                  <Row title="Placement commission" desc="Your default commission on a successful placement">
                    <div className="flex items-center gap-3 w-48"><Slider data-testid="settings-commission" value={[s.agency.commission_pct]} onValueChange={([v]) => upd("agency", "commission_pct", v)} min={0} max={50} step={1} /><span className="text-teal-400 font-mono text-sm w-12 text-right">{s.agency.commission_pct}%</span></div>
                  </Row>
                </TabsContent>
              )}
            </>}

            {/* ---------- ACCOUNT (shared) ---------- */}
            <TabsContent value="account" className="mt-0">
              <Row title={tr("settings.emailLbl")} desc={tr("settings.emailD")}><span className="text-slate-300 text-sm font-mono">{user?.email}</span></Row>
              <Row title={tr("settings.roleLbl")} desc={tr("settings.roleD")}><span className="text-teal-300 text-sm capitalize px-3 py-1 rounded-full bg-teal-500/10 border border-teal-500/20">{user?.role?.replace("_", " ")}</span></Row>
              <Row title={tr("settings.twofa")} desc={tr("settings.twofaD")}><span className="text-slate-500 text-xs">{tr("settings.comingSoon")}</span></Row>
            </TabsContent>
          </div>
        </Tabs>

        <div className="mt-6">
          <Button data-testid="settings-save-button" onClick={save} disabled={saving} className="bg-gradient-to-r from-teal-400 to-cyan-500 text-slate-900 font-semibold hover:opacity-90">
            <Save size={16} className="mr-2" /> {saving ? tr("settings.saving") : tr("settings.save")}
          </Button>
        </div>
      </div>
    </div>
  );
}
